<script setup lang="ts">
import { reactive, ref } from "vue";
import { throttle } from "lodash-es";
import { useContrastColor, useResolvedColor } from "@/helpers/utility/Color";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ColorPickerPopout from "@core/ui/ColorPickerPopout.vue";

type Color = string | null;
interface Props {
  value: Color;
  extraColors?: Color[];
  defaultValue?: NonNullable<Color>;
  transparent?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  value: null,
  extraColors: () => [],
  defaultValue: "var(--color-background-deep)",
  transparent: true
});
const emit = defineEmits<{
  (e: "update:value", value: Color): void;
}>();

const button = ref<HTMLButtonElement>();

const safeColor = useResolvedColor(() => props.value ?? props.defaultValue);
const contrastColor = useContrastColor(safeColor);

const throttledUpdateColor = throttle((color: Color) => {
  emit("update:value", color);
}, 500);

const colorPicker = reactive({
  open: false,
  x: 0,
  y: 0
});

function openColorPicker(e: MouseEvent) {
  colorPicker.open = true;
  if (button.value != null) {
    const buttonRect = button.value.getBoundingClientRect();
    colorPicker.x = buttonRect.left;
    colorPicker.y = buttonRect.bottom;
  } else {
    colorPicker.x = e.clientX;
    colorPicker.y = e.clientY;
  }
}
</script>

<template>
  <button
    ref="button"
    type="button"
    class="button-icon input-color-background"
    :title="$t('labels.backgroundColor')"
    :style="{ backgroundColor: safeColor }"
    v-bind="$attrs"
    @click.prevent="openColorPicker"
  >
    <lucide-icon name="paintbrush" :style="{ color: contrastColor }" />
  </button>

  <color-picker-popout
    v-if="colorPicker.open"
    :value="value"
    :x="colorPicker.x"
    :y="colorPicker.y"
    :extra-colors="extraColors"
    :null-allowed="true"
    :transparent="transparent"
    @update:value="throttledUpdateColor?.($event)"
    @close="colorPicker.open = false"
  />
</template>

<style lang="scss">
.input-color-background {
  border: 1px solid var(--color-border);

  &:active {
    border-color: var(--color-action);
  }
}
</style>
